// A page left open comes back current (@upland/shared/fresh): on return to
// the foreground, a new deploy reloads the page and a page hidden ten
// minutes reloads unless it has unsaved edits. Every page imports this
// module, so every page gets it. The reload button shows only in
// installed-app windows.
import { watchFresh } from "@upland/shared/fresh";
if (typeof document !== "undefined") watchFresh();

const TOKEN_KEY = "proposals_token";
const USER_KEY = "proposals_user";

export interface UserInfo {
	id: string;
	email: string;
	name: string;
	role: string;
}

export function getToken(): string | null {
	return localStorage.getItem(TOKEN_KEY);
}

export function getUser(): UserInfo | null {
	const raw = localStorage.getItem(USER_KEY);
	return raw ? JSON.parse(raw) : null;
}

export function setAuth(token: string, user: UserInfo) {
	localStorage.setItem(TOKEN_KEY, token);
	localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(USER_KEY);
}

export function requireAuth(): string {
	const token = getToken();
	if (!token) {
		window.location.href = "/";
		throw new Error("Not authenticated");
	}
	return token;
}

async function handleResponse(res: Response, path: string): Promise<unknown> {
	if (res.status === 401 && path !== "/api/login") {
		clearAuth();
		window.location.href = "/";
		throw new Error("Unauthorized");
	}

	const text = await res.text();
	let data: unknown;
	try {
		data = JSON.parse(text);
	} catch {
		throw new Error(res.status === 502 ? "Request timed out — try again" : `Server error (${res.status})`);
	}

	if (!res.ok) throw new Error((data as { error: string }).error || "Request failed");
	return data;
}

async function request(method: string, path: string, body?: unknown): Promise<unknown> {
	const token = getToken();
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	if (token) headers["Authorization"] = `Bearer ${token}`;

	let res: Response;
	try {
		res = await fetch(path, {
			method,
			headers,
			body: body !== undefined ? JSON.stringify(body) : undefined,
		});
	} catch {
		throw new Error("Network error — check your connection and try again");
	}

	return handleResponse(res, path);
}

export async function uploadFile(proposalId: string, path: string, file: File): Promise<unknown> {
	const token = requireAuth();
	const url = `/api/proposals/${proposalId}/file?path=${encodeURIComponent(path)}`;
	const res = await fetch(url, {
		method: "PUT",
		headers: {
			"Authorization": `Bearer ${token}`,
			"Content-Type": file.type || "application/octet-stream",
		},
		body: file,
	});
	return handleResponse(res, url);
}

export const api = {
	login: (username: string, password: string) => request("POST", "/api/login", { username, password }),
	logout: () => request("POST", "/api/logout").catch(() => null),

	listProposals: () => request("GET", "/api/proposals"),
	getProposal: (id: string) => request("GET", `/api/proposals/${id}`),
	createProposal: (data: { title: string; client_name?: string; client_email?: string }) => request("POST", "/api/proposals", data),
	updateProposal: (id: string, data: Record<string, unknown>) => request("PUT", `/api/proposals/${id}`, data),
	deleteProposal: (id: string) => request("DELETE", `/api/proposals/${id}`),
	addRecipient: (id: string, name: string, email?: string) => request("POST", `/api/proposals/${id}/recipients`, { name, email }),
	deleteRecipient: (id: string, rid: string) => request("DELETE", `/api/proposals/${id}/recipients/${rid}`),
	sendRecipient: (id: string, rid: string, note?: string) => request("POST", `/api/proposals/${id}/recipients/${rid}/send`, note ? { note } : {}),
	deleteFile: (id: string, path: string) => request("DELETE", `/api/proposals/${id}/file?path=${encodeURIComponent(path)}`),
};
